<template>
  <!--
    The visit checklist for one patient on the board: Schedule View's own VisitChecklistCard in a popover anchored to
    the alert icon that was tapped (a bottom sheet under 700px). Mounted only while open (the page v-ifs it), so the
    position is measured once, when it appears.
  -->
  <div class="ops-cl" :class="`ops-cl--${variant}`" data-test="ops-checklist">
    <div v-if="variant === 'pop'" class="ops-cl__shield" data-test="ops-checklist-shield" @click="$emit('close')"></div>
    <div v-else class="ops-cl__backdrop" data-test="ops-checklist-backdrop" @click="$emit('close')"></div>

    <VisitChecklistCard
      ref="card"
      class="ops-cl__card"
      :class="`ops-cl__card--${variant}`"
      :style="cardStyle"
      :appt="appt"
      :categories="categories"
      :checked="checked"
      :variant="variant"
      :focus-category="focusCategory"
      :accent="accent"
      :op-name="opName"
      :provider-name="providerName"
      :status-text="statusText"
      :bucket="bucket"
      :pending="pending"
      :incomplete="incomplete"
      @toggle="$emit('toggle', $event)"
      @open-tab="$emit('open-tab', $event)"
      @close="$emit('close')"
    />
  </div>
</template>

<script>
/**
 * Practice Ops' mount of the visit checklist card (plan §4). It is ChecklistLayer's job without its Schedule View
 * page state: ChecklistLayer needs the day's filtered appointments, the unfiltered day, the bucket map and the page's
 * checklists, none of which Practice Ops has — so the card is mounted directly, fed by the page:
 *
 *   appt        utils/scheduleView/visitAppt.js visitAppointment(board card + the live details)
 *   categories  checklist.js checklistsForDay(...) — the SAME function Schedule View builds its checklists with
 *   checked     itemId → true, from the store's ticks (scheduleView.checks)
 *
 * Events (the page wires them): toggle({ itemKey, checked }) → scheduleView/toggleCheck (main-service
 * /schedule-view-check, exactly as ChecklistLayer does), open-tab(tab) → the patient's profile, close.
 *
 * Position: `hoverCardPosition` (utils/scheduleView/layout.js) beside the tapped icon, measured against the card's real
 * height. Escape, a scroll outside the card, a resize or a tap on the shield / backdrop close it. z-index 10000: above
 * the AI agent's floating button (z 9999).
 *
 * ⛔ No prop is named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js computeds shadow them).
 */
import VisitChecklistCard from '@/pages/ScheduleView/components/VisitChecklistCard.vue';
import { hoverCardPosition, HOVER_CARD_WIDTH } from '@/utils/scheduleView/layout';
import { PHONE_MAX_WIDTH } from '@/utils/practiceOps/viewPref';

export default {
  name: 'OpsChecklistPopover',

  components: { VisitChecklistCard },

  props: {
    appt: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
    checked: { type: Object, default: () => ({}) },
    /** The tapped icon's viewport rect ({ top, left, right, bottom, width, height }), or null (a sheet needs none). */
    anchor: { type: Object, default: null },
    focusCategory: { type: String, default: null },
    accent: { type: String, default: '#9ca4a2' },
    opName: { type: String, default: '' },
    providerName: { type: String, default: '' },
    statusText: { type: String, default: '' },
    bucket: { type: String, default: 'arrived' },
    /** The alerts have not arrived yet: an empty checklist must not claim the visit is clean. */
    pending: { type: Boolean, default: false },
    /** Some alert rules failed to run. */
    incomplete: { type: Boolean, default: false },
  },

  data() {
    return {
      variant: this.pickVariant(),
      placement: null,
      measuredHeight: 0,
    };
  },

  computed: {
    cardStyle() {
      if (this.variant === 'sheet') return { maxHeight: '88%' };
      // Hidden (but laid out, so it can be measured) until the first placement.
      return this.placement || {
        top: '0px', left: '0px', width: `${HOVER_CARD_WIDTH}px`, maxHeight: 'calc(100vh - 20px)', visibility: 'hidden',
      };
    },
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, true);
    window.addEventListener('resize', this.onResize);
    this.$nextTick(this.place);
  },

  updated() {
    // The card can grow under us (a tick or a live refresh adds a line): re-place, but only on a real change.
    const card = this.$refs.card;
    if (this.placement && card && card.$el && Math.abs(card.$el.offsetHeight - this.measuredHeight) > 1) this.place();
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    pickVariant() {
      const width = typeof window !== 'undefined' ? window.innerWidth : 0;
      return width > 0 && width < PHONE_MAX_WIDTH ? 'sheet' : 'pop';
    },

    place() {
      const card = this.$refs.card;
      if (!card || !card.$el || this.variant === 'sheet') return;
      const height = card.$el.offsetHeight;
      this.measuredHeight = height;
      const rect = this.anchor || {
        top: 80, left: 80, right: 80, bottom: 80, width: 0, height: 0,
      };
      this.placement = Object.assign(
        { maxHeight: 'calc(100vh - 20px)' },
        hoverCardPosition(rect, { width: window.innerWidth, height: window.innerHeight }, height)
      );
    },

    onKeydown(e) {
      if (e && e.key === 'Escape') this.$emit('close');
    },

    onScroll(e) {
      // The card's own body scrolls (it has a max-height); that must not close it.
      const card = this.$refs.card && this.$refs.card.$el;
      const target = e && e.target;
      if (card && target && target.nodeType && card.contains(target)) return;
      this.$emit('close');
    },

    onResize() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
// A click-through frame the size of the screen; the shield / backdrop / card opt back in.
.ops-cl {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  // Above the AI agent's floating button (agent-panel.css, 9999 !important), as Schedule View's layer is.
  z-index: 10000;
}

.ops-cl__shield {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 59;
  pointer-events: auto;
}

.ops-cl__backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  pointer-events: auto;
  background: rgba(12, 15, 14, 0.32);
}

.ops-cl__card {
  pointer-events: auto;
  font-family: inherit;
}

.ops-cl__card--pop {
  position: fixed;
  z-index: 60;
  max-width: calc(100vw - 16px);
}

.ops-cl__card--sheet {
  position: fixed;
  top: auto;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10001;
  width: auto;
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
